<script setup lang="ts">
import { computed, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { ClipboardCheck, ChevronRight, X } from 'lucide-vue-next'
import { usePendingEditAssignments } from '@/composables/queries/edit-assignments/usePendingEditAssignments'
import { useAcknowledgeEditAssignment } from '@/composables/mutations/edit-assignments/useAcknowledgeEditAssignment'
import type { PendingEditAssignment } from '@/api/resources/edit-assignments'

/**
 * App-wide "are you working this edit?" prompt.
 *
 * Mounted once in app.js alongside the Inertia app (not inside AppLayout), so it renders on every
 * page and survives navigation. Renders nothing when the editor has nothing to confirm — which is
 * every user who isn't an editor with an unanswered auto-assignment.
 */

const SESSION_KEY = 'edit-assignment-ack-dismissed'

const page = usePage()
const { t } = useI18n()

const isAuthenticated = computed(() => !!page.props.user)

const query = usePendingEditAssignments(isAuthenticated)
const acknowledge = useAcknowledgeEditAssignment()

// Dismissed for this browser session only. The clock keeps running server-side, so "decide later"
// never means "this went away" — it just stops the prompt following them around this session.
const dismissed = ref<number[]>(readDismissed())

function readDismissed(): number[] {
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY)
        return raw ? (JSON.parse(raw) as number[]) : []
    } catch {
        return []
    }
}

function persistDismissed() {
    try {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(dismissed.value))
    } catch {
        // Private-mode / storage-disabled browsers: the prompt reappears on navigation, which is
        // the safe direction to fail in.
    }
}

const assignments = computed<PendingEditAssignment[]>(
    () => (query.data.value?.assignments ?? []).filter((a) => !dismissed.value.includes(a.request_revision_id)),
)

const current = computed<PendingEditAssignment | null>(() => assignments.value[0] ?? null)
const total = computed(() => assignments.value.length)

const reason = computed(() => {
    switch (current.value?.source) {
        case 'client_revision':
            return t('editAssignments.reason_client_revision')
        case 'qc_re_edit':
            return t('editAssignments.reason_qc_re_edit')
        case 'bid_award':
            return t('editAssignments.reason_bid_award')
        default:
            return t('editAssignments.reason_generic')
    }
})

const caption = computed(() => {
    const hours = current.value?.window_hours ?? 0
    return current.value?.deadline_at
        ? t('editAssignments.caption_with_deadline', { hours })
        : t('editAssignments.caption_no_deadline')
})

const timeLeft = computed(() => {
    const hours = current.value?.hours_remaining
    if (hours == null) return null
    return hours < 1
        ? t('editAssignments.time_left_soon')
        : t('editAssignments.time_left', { hours: Math.floor(hours) })
})

function confirmCurrent() {
    const revisionId = current.value?.request_revision_id
    if (revisionId == null || acknowledge.isPending.value) return
    acknowledge.mutate(revisionId)
}

function dismissCurrent() {
    const revisionId = current.value?.request_revision_id
    if (revisionId == null) return
    dismissed.value = [...dismissed.value, revisionId]
    persistDismissed()
}
</script>

<template>
    <Teleport to="body">
        <div v-if="current" class="eaa" role="dialog" aria-modal="false" aria-labelledby="eaa-title">
            <article class="eaa__card">
                <header class="eaa__header">
                    <div class="eaa__badge" aria-hidden="true">
                        <ClipboardCheck :size="22" :stroke-width="2" />
                    </div>
                    <div class="eaa__header-text">
                        <h2 id="eaa-title" class="eaa__title">
                            {{ total > 1 ? t('editAssignments.prompt_title_plural') : t('editAssignments.prompt_title') }}
                        </h2>
                        <p class="eaa__reason">{{ reason }}</p>
                    </div>
                    <button
                        type="button"
                        class="eaa__close"
                        :aria-label="t('editAssignments.decide_later')"
                        @click="dismissCurrent"
                    >
                        <X :size="18" :stroke-width="2" />
                    </button>
                </header>

                <div class="eaa__body">
                    <p class="eaa__media">{{ current.title || `Edit #${current.request_revision_id}` }}</p>
                    <p v-if="current.channel_name" class="eaa__channel">{{ current.channel_name }}</p>

                    <p class="eaa__caption">{{ caption }}</p>
                    <p v-if="timeLeft" class="eaa__deadline" role="status">{{ timeLeft }}</p>
                </div>

                <footer class="eaa__actions">
                    <button
                        type="button"
                        class="eaa__btn eaa__btn--primary"
                        :disabled="acknowledge.isPending.value"
                        @click="confirmCurrent"
                    >
                        {{ acknowledge.isPending.value ? t('editAssignments.marking') : t('editAssignments.mark_as_working') }}
                    </button>
                    <a v-if="current.proofing_url" :href="current.proofing_url" class="eaa__btn eaa__btn--ghost">
                        {{ t('editAssignments.open_edit') }}
                        <ChevronRight :size="15" :stroke-width="2" />
                    </a>
                    <button
                        type="button"
                        class="eaa__btn eaa__btn--ghost"
                        :disabled="acknowledge.isPending.value"
                        @click="dismissCurrent"
                    >
                        {{ t('editAssignments.decide_later') }}
                    </button>
                    <span v-if="total > 1" class="eaa__counter">
                        {{ t('editAssignments.counter', { current: 1, total }) }}
                    </span>
                </footer>
            </article>
        </div>
    </Teleport>
</template>

<style scoped>
/* Plain scoped CSS, no Tailwind dependency — this renders on every page, including the
   Bootstrap-only ones. Anchored bottom-right rather than a centered takeover: the editor must be
   able to keep working (and look at the edit) while deciding. */
.eaa {
    position: fixed;
    right: 1.25rem;
    bottom: 1.25rem;
    left: 1.25rem;
    z-index: 2147482000;
    display: flex;
    justify-content: flex-end;
    pointer-events: none;
}

.eaa__card {
    width: 100%;
    max-width: 26rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    pointer-events: auto;
}

.eaa__header {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1rem 0.5rem;
}

.eaa__badge {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: #fef3c7;
    color: #b45309;
}

.eaa__header-text {
    min-width: 0;
    flex: 1 1 auto;
}

.eaa__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    color: #111827;
}

.eaa__reason {
    margin: 0.125rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #6b7280;
}

.eaa__close {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
}

.eaa__close:hover {
    color: #111827;
    background: #f3f4f6;
}

.eaa__body {
    padding: 0.25rem 1rem 0.75rem;
}

.eaa__media {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #111827;
    overflow-wrap: anywhere;
}

.eaa__channel {
    margin: 0.125rem 0 0;
    font-size: 0.8125rem;
    color: #6b7280;
}

.eaa__caption {
    margin: 0.75rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #4b5563;
}

.eaa__deadline {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #b45309;
}

.eaa__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    padding: 0.75rem 1rem 1rem;
    border-top: 1px solid #f3f4f6;
    background: #f9fafb;
}

.eaa__btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.875rem;
    border: 0;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
}

.eaa__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.eaa__btn--primary {
    background: #047857;
    color: #ffffff;
}

.eaa__btn--primary:hover:not(:disabled) {
    background: #065f46;
}

.eaa__btn--primary:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 2px;
}

.eaa__btn--ghost {
    background: transparent;
    color: #4b5563;
}

.eaa__btn--ghost:hover:not(:disabled) {
    color: #111827;
    background: #f3f4f6;
}

.eaa__counter {
    margin-left: auto;
    font-size: 0.75rem;
    color: #9ca3af;
}
</style>
